import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

const CustomTimeline = () => {
  const { t } = useTranslation();
  const [customEvents, setCustomEvents] = useState([]);
  const [decks, setDecks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    year: '',
    description: '',
    summary: '',
    category: '',
    links: [''],
    disableAI: false,
    bulletPointsSummary: false,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [insights, setInsights] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, decksRes] = await Promise.all([
        axios.get('https://amora-backend.onrender.com/api/student/custom-timeline'),
        axios.get('https://amora-backend.onrender.com/api/student/decks'),
      ]);
      setCustomEvents(eventsRes.data);
      setDecks(decksRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post('https://amora-backend.onrender.com/api/student/custom-timeline', newEvent);
      toast.success(t('customTimeline.toast.addSuccess'));
      setNewEvent({
        title: '',
        year: '',
        description: '',
        summary: '',
        category: '',
        links: [''],
        disableAI: false,
        bulletPointsSummary: false,
      });
      setShowAddForm(false);
      fetchData();
    } catch (err) {
      console.error('Failed to add event:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('customTimeline.card.delete'))) return;
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/student/custom-timeline/${id}`);
      toast.success(t('customTimeline.toast.deleteSuccess'));
      fetchData();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };
  const openModal = async (event) => {
    setSelectedEvent(event);
    setInsights(null);

    if (event.disableAI) {
      console.log("🛑 AI summary disabled for:", event.title);
      return;
    }

    try {
      console.log("🔍 Fetching AI summary for:", event.title);
      const res = await axios.post('https://amora-ai-service.onrender.com/research-assistant', {
        title: event.title,
        description: event.description,
      });

      console.log("✅ AI summary response:", res.data);
      setInsights(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch AI insights:', err);
      setInsights({ ai_summary: t('customTimeline.modal.fetchError') });
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setEditForm({
      ...event,
      links: event.links || [''],
      bulletPointsSummary: event.bulletPointsSummary || false,
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://amora-backend.onrender.com/api/student/custom-timeline/${editForm._id}`, editForm);
      toast.success(t('customTimeline.toast.updateSuccess'));
      setEditingEvent(null);
      setEditForm(null);
      await fetchData();
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error(t('customTimeline.toast.updateError'));
    }
  };

  const handleAddToDeck = async (event, deckTitle) => {
    try {
      await axios.post(`https://amora-backend.onrender.com/api/student/decks/${deckTitle}/events`, event);
      toast.success(`${t('customTimeline.card.addToDeck')}: ${deckTitle}`);
    } catch (err) {
      console.error('Failed to add to deck:', err);
    }
  };
  return (
    <div className="p-6 text-black">
      <ToastContainer position="top-right" autoClose={2000} />
      <h2 className="text-xl font-semibold uppercase border-b border-black mb-6 pb-2">
        {t('customTimeline.heading')}
      </h2>

      {/* Add Event Button + Form */}
      <div className="flex justify-center mb-10">
        <div className="w-full max-w-2xl">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="w-full border border-black bg-white text-black px-4 py-2 font-bold uppercase tracking-wide hover:bg-black hover:text-white transition mb-6"
          >
            {showAddForm ? t('customTimeline.cancelButton') : t('customTimeline.addButton')}
          </button>

          <AnimatePresence>
            {showAddForm && (
              <motion.form
                onSubmit={handleAddEvent}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
               className="bg-white border border-black p-6 space-y-4 shadow-none"
              >
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder={t('customTimeline.form.title')}
                  className="w-full border border-black bg-white text-black px-3 py-2 uppercase tracking-wide font-medium"
                  required
                />
                <input
                  type="number"
                  value={newEvent.year}
                  onChange={(e) => setNewEvent({ ...newEvent, year: e.target.value })}
                  placeholder={t('customTimeline.form.year')}
                  className="w-full border border-black bg-white text-black px-3 py-2 uppercase tracking-wide font-medium"
                  required
                />
                         <input
                  type="text"
                  value={newEvent.category}
                  onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                  placeholder="Category"
                  className="w-full border border-black bg-white text-black px-3 py-2 uppercase tracking-wide font-medium"
                  required
                />
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder={t('customTimeline.form.descriptionShort')}
                  className="w-full border border-black bg-white text-black px-3 py-2 uppercase tracking-wide font-medium"
                  rows="2"
                  required
                />
                <textarea
                  value={newEvent.summary}
                  onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                  placeholder={t('customTimeline.form.descriptionLong')}
                  className="w-full border border-black bg-white text-black px-3 py-2 uppercase tracking-wide font-medium"
                  rows="4"
                />
                {newEvent.links.map((link, idx) => (
                  <input
                    key={idx}
                    value={link}
                    onChange={(e) => {
                      const updated = [...newEvent.links];
                      updated[idx] = e.target.value;
                      setNewEvent({ ...newEvent, links: updated });
                    }}
                    placeholder={`Link ${idx + 1}`}
                    className="w-full border border-black bg-white text-black px-3 py-2 font-bold tracking-wide uppercase placeholder:text-gray-400 mt-2"
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setNewEvent(prev => ({ ...prev, links: [...prev.links, ''] }))}
                  className="text-black-400 hover:underline text-sm"
                >
                  {t('customTimeline.form.addLink')}
                </button>

                <label className="flex items-center gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={newEvent.disableAI}
                    onChange={(e) => setNewEvent({ ...newEvent, disableAI: e.target.checked })}
                  />
                  {t('customTimeline.form.disableAI')}
                </label>

                <label className="flex items-center gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={newEvent.bulletPointsSummary}
                    onChange={(e) => setNewEvent({ ...newEvent, bulletPointsSummary: e.target.checked })}
                  />
                  {t('customTimeline.form.bulletPoints')}
                </label>

                <button
                  type="submit"
                  className="w-full border border-black bg-white text-black px-4 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition mt-4"
                >
                  {t('customTimeline.submitButton')}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Timeline Cards */}
      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
  {customEvents.map((event) => (
  <div
  key={event._id}
  onClick={() => openModal(event)}
  className="group border border-black bg-white text-black hover:bg-black hover:text-white p-4 transition font-bold tracking-wide uppercase"
>
      <div>
      <h3 className="text-xl font-bold group-hover:text-white mb-1">{event.title}</h3>
<p className="text-sm group-hover:text-white mb-1">{event.year}</p>
<p className="text-sm group-hover:text-white mb-1">{event.category}</p>
<p className="text-sm group-hover:text-white line-clamp-3">{event.description}</p>
      </div>

      <div className="flex flex-col gap-2 mt-5 w-full">

        <button
          onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
          className="w-full border border-black bg-white text-black text-xs px-3 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition text-left"
        >
           {t('customTimeline.card.edit')}
        </button>


<select
  onClick={(e) => e.stopPropagation()}
  onChange={(e) => handleAddToDeck(event, e.target.value)}
  defaultValue=""
  className="w-full border border-black bg-white text-black text-xs px-3 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition text-left"
>
  <option value="" disabled>{t('customTimeline.card.addToDeck')}</option>
  {decks.map(deck => (
    <option key={deck.title} value={deck.title}>{deck.title}</option>
  ))}
</select>



        <button
          onClick={(e) => { e.stopPropagation(); handleDelete(event._id); }}
          className="w-full border border-black bg-white text-black text-xs px-3 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition text-left"
        >
           {t('customTimeline.card.delete')}
        </button>
      </div>
    </div>
  ))}
</div>


      {/* View Modal */}
      {selectedEvent && (
       <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 px-4">
         <div className="bg-white text-black border border-black p-6 max-w-lg w-full relative shadow-none rounded-none">   
            <button
              onClick={() => { setSelectedEvent(null); setInsights(null); }}
              className="absolute top-3 right-4 text-black text-xl font-bold"
            >
              ✖
            </button>

            <h3 className="text-xl font-bold uppercase border-b border-black mb-2">{selectedEvent.title}</h3>
<p className="text-sm text-black mb-1">{selectedEvent.year}</p>
<p className="text-sm text-black mb-1">{selectedEvent.category}</p>
<p className="text-sm text-black mt-3">{selectedEvent.description}</p>

            {selectedEvent.summary && (
              <>
                <h4 className="text-lg font-semibold text-white-300 mt-6">{t('customTimeline.modal.summary')}</h4>
                <p className="text-sm text-black-300 whitespace-pre-wrap mt-2">{selectedEvent.summary}</p>
              </>
            )}

            {!selectedEvent?.disableAI && insights && Object.keys(insights).length > 0 ? (
              <>
                {insights.ai_summary && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-black-300">{t('customTimeline.modal.aiSummary')}</h4>
                    <p className="text-sm text-black-300 whitespace-pre-wrap">{insights.ai_summary}</p>
                  </div>
                )}
                {insights.bullet_points && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-white-300">{t('customTimeline.modal.bulletPoints')}</h4>
                    <ul className="list-disc list-inside text-black-400 text-sm">
                      {insights.bullet_points.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.did_you_know && (
                  <p className="text-black-400 text-sm italic mt-4">{t('customTimeline.modal.didYouKnow')} {insights.did_you_know}</p>
                )}
                {insights.recommended_searches && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-white-300">{t('customTimeline.modal.recommendations')}</h4>
                    <ul className="list-disc list-inside text-black-400 text-sm">
                      {insights.recommended_searches.map((term, idx) => (
                        <li key={idx}>{term}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insights.related_links && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-white-300">{t('customTimeline.modal.resources')}</h4>
                    <ul className="list-disc list-inside text-black-400 text-sm">
                      {insights.related_links.map((link, idx) => (
                        <li key={idx}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-black-300">
                            {link.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="text-red-400 text-sm mt-4">{t('customTimeline.modal.noInsights')}</p>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingEvent && (
  <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 px-4">
    <div className="bg-white text-black border border-black p-6 max-w-lg w-full relative shadow-none font-bold tracking-wide uppercase">
      <button
        onClick={() => setEditingEvent(null)}
        className="absolute top-3 right-4 text-black text-xl font-bold"
      >
        ✖
      </button>

      <form onSubmit={handleSaveEdit} className="space-y-4">
        <input
          type="text"
          value={editForm.title}
          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          placeholder={t('customTimeline.form.title')}
          className="w-full border border-black bg-white text-black px-3 py-2"
          required
        />
        <input
          type="number"
          value={editForm.year}
          onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
          placeholder={t('customTimeline.form.year')}
          className="w-full border border-black bg-white text-black px-3 py-2"
          required
        />
        <input
          type="text"
          value={editForm.category}
          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
          placeholder={t('customTimeline.form.category')}
          className="w-full border border-black bg-white text-black px-3 py-2"
          required
        />
        <textarea
          value={editForm.description}
          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          placeholder={t('customTimeline.form.descriptionShort')}
          rows="2"
          className="w-full border border-black bg-white text-black px-3 py-2"
        />
        <textarea
          value={editForm.summary}
          onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
          placeholder={t('customTimeline.form.descriptionLong')}
          rows="4"
          className="w-full border border-black bg-white text-black px-3 py-2"
        />
        {editForm.links.map((link, idx) => (
          <input
            key={idx}
            value={link}
            onChange={(e) => {
              const updated = [...editForm.links];
              updated[idx] = e.target.value;
              setEditForm({ ...editForm, links: updated });
            }}
            placeholder={`Link ${idx + 1}`}
            className="w-full border border-black bg-white text-black px-3 py-2 mt-2"
          />
        ))}
        <button
          type="button"
          onClick={() => setEditForm(prev => ({ ...prev, links: [...prev.links, ''] }))}
          className="text-black hover:underline text-sm"
        >
          {t('customTimeline.form.addLink')}
        </button>
        <label className="flex items-center gap-2 text-sm mt-4">
          <input
            type="checkbox"
            checked={editForm.disableAI}
            onChange={(e) => setEditForm({ ...editForm, disableAI: e.target.checked })}
          />
          {t('customTimeline.form.disableAI')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editForm.bulletPointsSummary}
            onChange={(e) => setEditForm({ ...editForm, bulletPointsSummary: e.target.checked })}
          />
          {t('customTimeline.form.bulletPoints')}
        </label>
        <button
          type="submit"
          className="w-full border border-black bg-white text-black px-4 py-2 uppercase font-bold tracking-wide hover:bg-black hover:text-white transition mt-4"
        >
          {t('customTimeline.form.saveEdit')}
        </button>
      </form>
    </div>
  </div>
)}
    </div>
  );
};

export default CustomTimeline;
